/**
 * Visible, dismissible notice for a refused command or join (test-spec §6.6.2
 * default 11: silent rejection is indistinguishable from a bug at the table).
 *
 * Renders the honest per-code copy from `rejectionCopy` into an alert the
 * player can dismiss. E2E-04/E2E-05 assert a rejection indication appears
 * (and the token snaps back via the optimistic-echo layer, which is net/'s
 * job, not this module's). `data-reason` carries the raw code so tests can
 * assert on the code (CMD-02's reason code) while the human reads the sentence.
 */

import { rejectionMessage, type RejectionCode } from "./rejectionCopy.js";

export interface RejectionNoticeOptions {
  onDismiss?(): void;
}

/**
 * Render (or replace) the rejection notice in `root`.
 */
export function renderRejectionNotice(
  root: HTMLElement,
  code: RejectionCode,
  options: RejectionNoticeOptions = {},
): void {
  root.replaceChildren();

  const notice = document.createElement("div");
  notice.dataset["testid"] = "rejection";
  notice.dataset["reason"] = code;
  notice.setAttribute("role", "alert");

  const message = document.createElement("span");
  message.className = "rejection-message";
  message.textContent = rejectionMessage(code);
  notice.appendChild(message);

  const dismiss = document.createElement("button");
  dismiss.type = "button";
  dismiss.dataset["testid"] = "dismiss-rejection";
  dismiss.textContent = "Dismiss";
  dismiss.setAttribute("aria-label", "Dismiss message");
  dismiss.addEventListener("click", () => {
    root.replaceChildren();
    options.onDismiss?.();
  });
  notice.appendChild(dismiss);

  root.appendChild(notice);
}
